<template>
  <div>
    <FormSectionContainer
      index="1"
      title="Összetett feltételek hozzáadása"
      :key="selectedComplexFilters.length > 0 ? selectedComplexFilters[0] : 'selectedComplexFilters'"
    >
      <ComplexFilterSelector
        @new-selected-complex-filters="handleComplexFiltersSelection"
        :complexFilters="complexFilters"
        :preSelection="selectedComplexFilters"
      />
    </FormSectionContainer>

    <FormSectionContainer
      index="2"
      title="Gazdasági formakód szerinti feltétel megadása"
      :key="selectedGfos ? selectedGfos[0] : 'selectedGfos'"
    >
      <MultipleSelect
        :options="this.gfoList"
        label="Megengedett cégformák"
        placeholder="Válasszon cégformákat"
        @on-item-select="handleGfoSelect($event)"
        :preSelection="selectedGfos"
      />
    </FormSectionContainer>

    <FormSectionContainer index="3" title="Gazdasági mutatók szerinti feltételek megadása">
      <b-row>
        <b-col cols="12" sm="6" md="6" lg="6" xl="6" class="mb-3">
          <label for="economicData.minHeadCount">Minimum létszám</label>
          <b-form-input
            id="economicData.minHeadCount"
            v-model="economicData.minHeadCount"
            @keypress="isNumber($event, false)"
            placeholder="Adja meg az elvárt létszám minimumát"
            trim
          ></b-form-input
        ></b-col>
        <b-col cols="12" sm="6" md="6" lg="6" xl="6" class="mb-3">
          <label for="economicData.maxHeadCount">Maximum létszám</label>
          <b-form-input
            id="economicData.maxHeadCount"
            v-model="economicData.maxHeadCount"
            @keypress="isNumber($event, false)"
            placeholder="Adja meg az elvárt létszám maximumát"
            trim
          ></b-form-input>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" sm="6" md="6" lg="6" xl="6" class="mb-3">
          <label for="economicData.minBalanceSheetTotal">Minimum mérlegfőösszeg</label>
          <b-form-input
            id="economicData.minBalanceSheetTotal"
            v-model="economicData.minBalanceSheetTotal"
            @keypress="isNumber($event, true)"
            placeholder="Adja meg az elvárt mérlegfőösszeg minimumát"
            trim
          ></b-form-input>
        </b-col>
        <b-col cols="12" sm="6" md="6" lg="6" xl="6" class="mb-3">
          <label for="economicData.maxBalanceSheetTotal">Maximum mérlegfőösszeg</label>
          <b-form-input
            id="economicData.maxBalanceSheetTotal"
            v-model="economicData.maxBalanceSheetTotal"
            @keypress="isNumber($event, true)"
            placeholder="Adja meg az elvárt mérlegfőösszeg maximumát"
            trim
          ></b-form-input>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" sm="6" md="6" lg="6" xl="6" class="mb-3">
          <label for="economicData.minIncome">Minimum nettó árbevétel</label>
          <b-form-input
            id="economicData.minIncome"
            v-model="economicData.minIncome"
            @keypress="isNumber($event, true)"
            placeholder="Adja meg az elvárt nettó árbevétel minimumát"
            trim
          ></b-form-input
        ></b-col>
        <b-col cols="12" sm="6" md="6" lg="6" xl="6" class="mb-3">
          <label for="economicData.maxIncome">Maximum nettó árbevétel</label>
          <b-form-input
            id="economicData.maxIncome"
            v-model="economicData.maxIncome"
            @keypress="isNumber($event, true)"
            placeholder="Adja meg az elvárt nettó árbevétel maximumát"
            trim
          ></b-form-input>
        </b-col>
      </b-row>

      <label for="economicData.closedBusinessYears">Minimum lezárt üzleti évek száma</label>
      <b-form-input
        id="economicData.closedBusinessYears"
        v-model="economicData.closedBusinessYears"
        @keypress="isNumber($event, true)"
        placeholder="Minimum lezárt üzleti évek száma"
        trim
        class="mb-2"
      ></b-form-input>
    </FormSectionContainer>

    <FormSectionContainer index="4" title="Exportértékesítés szerinti feltétel megadása">
      <b-row>
        <b-col cols="12" sm="6" md="6" lg="6" xl="6" class="d-flex align-items-center">
          <p class="export-description">
            A vállalkozás exportból származó árbevétele a teljes árbetvétel minimum százaléka.
          </p>
        </b-col>
        <b-col cols="12" sm="6" md="6" lg="6" xl="6">
          <label for="exportData.minPercent">Minimum százalék mértéke</label>
          <div :class="`${this.touched ? '' : 'input-symbol-percent'}`">
            <b-form-input
              id="exportData.minPercent"
              v-model="exportData.minPercent"
              @keypress="isNumber($event, true)"
              placeholder="Minimum százalék mértéke"
              trim
            ></b-form-input>
          </div>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" sm="6" md="6" lg="6" xl="6" class="d-flex align-items-center">
          <p class="export-description">
            A vállalkozás exportból származó minimum árbevétele.
          </p>
        </b-col>
        <b-col cols="12" sm="6" md="6" lg="6" xl="6"
          ><label for="exportData.minIncome">Minimum árbevétel mértéke</label>
          <div :class="`${this.touched ? '' : 'input-symbol-forint'}`">
            <b-form-input
              id="exportData.minIncome"
              v-model="exportData.minIncome"
              @keypress="isNumber($event, true)"
              placeholder="Minimum árbevétel mértéke"
              trim
            ></b-form-input>
          </div>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" sm="6" md="6" lg="6" xl="6" class="d-flex align-items-center">
          <p class="export-description">
            A vállalkozás exportból származó maximum árbevétele.
          </p>
        </b-col>
        <b-col cols="12" sm="6" md="6" lg="6" xl="6"
          ><label for="exportData.maxIncome">Maximum árbevétel mértéke</label>
          <div :class="`${this.touched ? '' : 'input-symbol-forint'}`">
            <b-form-input
              id="exportData.maxIncome"
              v-model="exportData.maxIncome"
              @keypress="isNumber($event, true)"
              placeholder="Maximum árbevétel mértéke"
              trim
            ></b-form-input>
          </div>
        </b-col>
      </b-row>
    </FormSectionContainer>

    <FormSectionContainer
      index="5"
      title="Tevékenységkód szerinti feltétel megadása"
      :key="
        selectedTwoDigitTeaors
          ? selectedTwoDigitTeaors[0]
          : selectedThreeDigitTeaors
          ? selectedThreeDigitTeaors[0]
          : selectedFourDigitTeaors
          ? selectedFourDigitTeaors[0]
          : 'teaor'
      "
    >
      <b-form-group class="text-left">
        <b-row style="margin-top:10px;">
          <b-col class="radio-box" style="margin-left: 15px; ">
            <input type="radio" id="twoDigit" value="twoDigit" v-model="teaorType" />
            <label class="radio-text" for="twoDigit">Tevékenységkód megadása (Kétjegyű)</label>
          </b-col>
          <b-col cols="12" md="6" lg="6" xl="6">
            <MultipleTeaorSelect
              :disabled="teaorType !== 'twoDigit'"
              listType="twoDigit"
              :alreadySelected="selectedTwoDigitTeaors"
              title="Tevékenységkód megadása (Kétjegyű)"
              @selected-other-teaors="handleTwoDigitTeaorSelect($event)"
            />
          </b-col>
        </b-row>
        <b-row style="margin-top:10px;">
          <b-col class="radio-box" style="margin-left: 15px; ">
            <input type="radio" id="threeDigit" value="threeDigit" v-model="teaorType" />
            <label class="radio-text" for="threeDigit">Tevékenységkód megadása (Háromjegyű)</label>
          </b-col>
          <b-col cols="12" md="6" lg="6" xl="6">
            <MultipleTeaorSelect
              :disabled="teaorType !== 'threeDigit'"
              listType="threeDigit"
              :alreadySelected="selectedThreeDigitTeaors"
              title="Tevékenységkód megadása (Háromjegyű)"
              @selected-other-teaors="handleThreeDigitTeaorSelect($event)"
            />
          </b-col>
        </b-row>
        <b-row style="margin-top:10px;">
          <b-col class="radio-box" style="margin-left: 15px; ">
            <input type="radio" id="fourDigit" value="fourDigit" v-model="teaorType" />
            <label class="radio-text" for="fourDigit">Tevékenységkód megadása (Négyjegyű)</label>
          </b-col>
          <b-col cols="12" md="6" lg="6" xl="6">
            <MultipleTeaorSelect
              listType="fourDigit"
              :disabled="teaorType !== 'fourDigit'"
              :alreadySelected="selectedFourDigitTeaors"
              title="Tevékenységkód megadása (Négyjegyű)"
              @selected-other-teaors="handleFourDigitTeaorSelect($event)"
            />
          </b-col>
        </b-row>
      </b-form-group>
    </FormSectionContainer>

    <FormSectionContainer
      index="6"
      title="Megvalósítás helye szerinti feltétel megadása"
      :key="selectedZips ? selectedZips[0] : 'selectedZips'"
    >
      <b-col>
        <MultipleZipSelect @selected-other-zips="handleZipSelect($event)" :alreadySelected="selectedZips" />
      </b-col>
    </FormSectionContainer>

    <FormSectionContainer index="7" title="Vállalkozással kapcsolatos további logikai feltételek">
      <b-form-checkbox
        id="checkbox-1"
        v-model="additionalConditions.positiveEquity"
        name="checkbox-1"
        value="true"
        unchecked-value="false"
        class="mt-3"
      >
        A vállalkozásnak pozitív saját tőkével kell rendelkeznie
      </b-form-checkbox>

      <b-form-checkbox
        id="checkbox-2"
        v-model="additionalConditions.revenueFromAggriculture"
        name="checkbox-2"
        value="true"
        unchecked-value="false"
        class="mt-3"
      >
        A vállalkozás árbevételének minimum 50%-a mezőgazdasági tevékenységből kell származzon
      </b-form-checkbox>

      <b-form-checkbox
        id="checkbox-3"
        v-model="additionalConditions.isSupplier"
        name="checkbox-3"
        value="true"
        unchecked-value="false"
        class="mt-3"
      >
        A vállalkozásnak beszállítónak kell lennie
      </b-form-checkbox>
    </FormSectionContainer>

    <FormSectionContainer
      index="8"
      title="Támogatásokon való részvétel miatti kizárások"
      :key="restrictions ? restrictions[0] : 'restrictions'"
    >
      <p class="restriction-caption">
        Jelölje meg azokat a támogatásokat, melyek kizáró feltételei lehetnek a rögzített forrásnak.
      </p>
      <ApplicationRestrictions
        :data="this.applications"
        @application-restriction-ids="handleRestrictions($event)"
        :preSelection="restrictions"
      />
    </FormSectionContainer>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import FormSectionContainer from '../components/FormSectionContainer.vue'
import MultipleSelect from '../../../components/MultipleSelect.vue'
import ComplexFilterSelector from '../components/ComplexFiltersSelector.vue'
import { gfoOptions } from '../utils/gfoOptions'
import * as CompetitionApi from '../../../api/competition'
import ApplicationRestrictions from '../components/ApplicationRestrictions.vue'
import MultipleZipSelect from '../../FundMap/components/MultipleZipSelect.vue'
import MultipleTeaorSelect from '../../FundMap/components/MultipleTeaorSelect.vue'
import * as ComplexFilterApi from '../../../api/complexFilter'
export default {
  props: {},
  data() {
    return {
      complexFilters: [],
      selectedComplexFilters: [],
      selectedGfos: [],
      economicData: {
        minHeadCount: '',
        maxHeadCount: '',
        minBalanceSheetTotal: '',
        maxBalanceSheetTotal: '',
        minIncome: '',
        maxIncome: '',
        closedBusinessYears: ''
      },
      exportData: { minPercent: '', minIncome: '', maxIncome: '' },
      selectedTwoDigitTeaors: [],
      selectedThreeDigitTeaors: [],
      selectedFourDigitTeaors: [],
      selectedZips: [],
      additionalConditions: {
        positiveEquity: false,
        revenueFromAggriculture: false,
        isSupplier: false
      },
      restrictions: [],
      applications: [],
      gfoList: gfoOptions,
      touched: this.isTouched,
      teaorType: 'twoDigit'
    }
  },
  components: {
    FormSectionContainer,
    MultipleSelect,
    ComplexFilterSelector,
    ApplicationRestrictions,
    MultipleZipSelect,
    MultipleTeaorSelect
  },
  computed: {
    ...mapGetters(['newFundDto'])
  },
  methods: {
    isNumber(evt, isMinusAllowed) {
      const isStarting0Allowed = !this.$route.path.includes('/create-fund')
      const input = evt.target.value
      const keysAllowed = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
      const keyPressed = evt.key

      if (keyPressed === '-' && input.length === 0 && isMinusAllowed) {
        return
      }

      if (input.length === 0 && keyPressed === '0' && isStarting0Allowed) {
        evt.preventDefault()
        return
      }

      if (!keysAllowed.includes(keyPressed)) {
        evt.preventDefault()
      }
    },
    addCompetitionAttribute(key, value) {
      this.$store.dispatch('setFundDtoPropertyAction', { [key]: value })
    },
    deleteCompetitionAttribute(key) {
      this.$store.dispatch('deleteFundDtoPropertyAction', key)
    },
    handleComplexFiltersSelection(complexFilters) {
      this.selectedComplexFilters = complexFilters
    },
    handleGfoSelect(gfoArray) {
      this.selectedGfos = gfoArray
    },
    handleZipSelect(zipArray) {
      this.selectedZips = zipArray
    },
    handleTwoDigitTeaorSelect(twoDigitTeaorArray) {
      this.selectedTwoDigitTeaors = twoDigitTeaorArray
    },
    handleThreeDigitTeaorSelect(threeDigitTeaorArray) {
      this.selectedThreeDigitTeaors = threeDigitTeaorArray
    },
    handleFourDigitTeaorSelect(fourDigitTeaorArray) {
      this.selectedFourDigitTeaors = fourDigitTeaorArray
    },
    handleRestrictions(applications) {
      this.restrictions = applications
    }
  },
  watch: {
    economicData: {
      handler(value) {
        this.addCompetitionAttribute('economicData', value)
      },
      deep: true
    },
    exportData: {
      handler(value) {
        this.addCompetitionAttribute('exportData', value)
      },
      deep: true
    },
    additionalConditions: {
      handler(value) {
        this.addCompetitionAttribute('additionalConditions', value)
      },
      deep: true
    },
    restrictions() {
      this.addCompetitionAttribute('restrictions', { competitionIds: this.restrictions })
    },
    selectedComplexFilters() {
      this.addCompetitionAttribute('complexFilters', this.selectedComplexFilters)
    },
    selectedGfos() {
      this.addCompetitionAttribute('selectedGfos', this.selectedGfos)
    },
    selectedTwoDigitTeaors() {
      this.addCompetitionAttribute('twoDigitTeaors', this.selectedTwoDigitTeaors)
    },
    selectedThreeDigitTeaors() {
      this.addCompetitionAttribute('threeDigitTeaors', this.selectedThreeDigitTeaors)
    },
    selectedFourDigitTeaors() {
      this.addCompetitionAttribute('fourDigitTeaors', this.selectedFourDigitTeaors)
    },
    teaorType() {
      this.deleteCompetitionAttribute('twoDigitTeaors')
      this.deleteCompetitionAttribute('threeDigitTeaors')
      this.deleteCompetitionAttribute('fourDigitTeaors')
    },
    selectedZips() {
      this.addCompetitionAttribute('selectedZips', this.selectedZips)
    }
  },
  async beforeMount() {
    try {
      this.complexFilters = await ComplexFilterApi.listComplexFilters()
    } catch (e) {
      console.log(e)
    }
    try {
      this.applications = await CompetitionApi.getApplicationsForRestriction()
    } catch (e) {
      console.log(e)
    }

    const { competitionId } = this.$route.query
    if (competitionId) {
      try {
        const fundResult = await CompetitionApi.getCompetitionWithDetails(competitionId)

        if (fundResult.fundId.complexFilters) {
          this.selectedComplexFilters = fundResult.fundId.complexFilters
        }
        if (fundResult.fundId.selectedGfos) {
          this.selectedGfos = fundResult.fundId.selectedGfos
        }
        if (fundResult.fundId.economicData) {
          this.economicData = fundResult.fundId.economicData
        }
        if (fundResult.fundId.exportData) {
          this.exportData = fundResult.fundId.exportData
        }

        if (fundResult.fundId.twoDigitTeaors && fundResult.fundId.twoDigitTeaors.length > 0) {
          this.selectedTwoDigitTeaors = fundResult.fundId.twoDigitTeaors
          this.teaorType = 'twoDigit'
        }

        if (fundResult.fundId.threeDigitTeaors && fundResult.fundId.threeDigitTeaors.length > 0) {
          this.selectedThreeDigitTeaors = fundResult.fundId.threeDigitTeaors
          this.teaorType = 'threeDigit'
        }

        if (fundResult.fundId.fourDigitTeaors && fundResult.fundId.fourDigitTeaors.length > 0) {
          this.selectedFourDigitTeaors = fundResult.fundId.fourDigitTeaors
          this.teaorType = 'fourDigit'
        }
        if (fundResult.fundId.selectedZips) {
          this.selectedZips = fundResult.fundId.selectedZips
        }
        if (fundResult.fundId.additionalConditions) {
          this.additionalConditions = fundResult.fundId.additionalConditions
        }
        if (fundResult.fundId.restrictions.competitionIds) {
          this.restrictions = fundResult.fundId.restrictions.competitionIds
        }
      } catch (e) {
        console.log(e)
      }
    } else {
      if (this.newFundDto.complexFilters) {
        this.selectedComplexFilters = this.newFundDto.selectedComplexFilters
      }
      if (this.newFundDto.selectedGfos) {
        this.selectedGfos = this.newFundDto.selectedGfos
      }
      if (this.newFundDto.economicData) {
        this.economicData = this.newFundDto.economicData
      }
      if (this.newFundDto.exportData) {
        this.exportData = this.newFundDto.exportData
      }
      if (this.newFundDto.twoDigitTeaors) {
        this.selectedTwoDigitTeaors = this.newFundDto.twoDigitTeaors
      }
      if (this.newFundDto.threeDigitTeaors) {
        this.selectedThreeDigitTeaors = this.newFundDto.threeDigitTeaors
      }
      if (this.newFundDto.fourDigitTeaors) {
        this.selectedFourDigitTeaors = this.newFundDto.fourDigitTeaors
      }
      if (this.newFundDto.selectedZips) {
        this.selectedZips = this.newFundDto.selectedZips
      }
      if (this.newFundDto.additionalConditions) {
        this.additionalConditions = this.newFundDto.additionalConditions
      }
      if (this.newFundDto.restrictions) {
        this.restrictions = this.newFundDto.restrictions
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.export-description {
  font-size: 12px;
  margin-top: 30px;
}

.create-fund-error-border {
  border: 2px solid #ba3b46 !important;
}

.create-fund-error-message {
  color: #ba3b46;
  text-align: right;
  font-size: 14px;
}

.radio-text {
  font-weight: 400;
  font-size: 14px;
  color: #2d3436;
  text-transform: none;
}

input[type='radio'] {
  width: 20px;
  height: 20px;
  border: 1px solid #bbc1d3;
}

input[type='radio']:after {
  border-radius: 20px;
  content: '';
  display: inline-block;
}

input[type='radio']:checked:after {
  width: 20px;
  height: 20px;
  background-color: white;
  border: 6px solid #00903c;
}
.radio-box {
  display: flex;
  padding-top: 10px;
}
.restriction-caption {
  font-family: Roboto;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  text-align: left;
  margin-bottom: 30px;
}
</style>
